import React, { useMemo } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, Divider } from '@mui/material';
import autoTable from 'jspdf-autotable';
import { formatCurrency } from '../../../utils/financialCalculations';
import { lifeCarePlanCategoryConfig } from '../../../reports/LifeCarePlan/lifeCarePlanCategoryConfig';

const RenderCategoryTable = ({ title, data, columns }) => {
    if (!data || data.length === 0) {
        return <Typography sx={{ mt: 1, fontStyle: 'italic' }}>No {title.toLowerCase()} data available.</Typography>;
    }

    const totalAnnualCost = data.reduce((acc, item) => acc + (item.annualCost || 0), 0);

    return (
        <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>{title}</Typography>
            <TableContainer component={Paper} elevation={1} sx={{ maxHeight: 800 }}>
                <Table stickyHeader size="small" aria-label={`${title} table`}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.field}
                                    align={column.align || 'left'}
                                    style={{ minWidth: column.minWidth, fontWeight: 'bold', backgroundColor: '#eee' }}
                                >
                                    {column.headerName}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((item, index) => (
                            <TableRow key={item.id || index}>
                                {columns.map(col => (
                                    <TableCell key={col.field} align={col.align || 'left'}>
                                        {col.isCurrency ? formatCurrency(item[col.field]) : (item[col.field] || '-')}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow sx={{ '& > *': { fontWeight: 'bold', backgroundColor: '#f0f0f0' } }}>
                            <TableCell colSpan={columns.length - 1} align="right">Total Annual Cost</TableCell>
                            <TableCell align="right">{formatCurrency(totalAnnualCost)}</TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    );
};

const LifeCarePlanSummary = ({ reportData }) => {
    const { lifeCarePlan } = reportData || {};

    const { summary, categoryData } = useMemo(() => {
        if (!lifeCarePlan) {
            return { summary: {}, categoryData: {} };
        }

        const newSummary = {
            totalAnnualCost: 0,
            totalFutureValue: 0,
            totalPresentValue: 0,
        };

        const newCategoryData = {};

        Object.keys(lifeCarePlanCategoryConfig).forEach(category => {
            const items = lifeCarePlan[category] || [];
            newCategoryData[category] = {
                items,
                totalAnnualCost: items.reduce((acc, item) => acc + (item.annualCost || 0), 0),
            };
            newSummary.totalAnnualCost += newCategoryData[category].totalAnnualCost;
        });
        
        // TODO: Implement Present Value Calculation
        // TODO: Implement Future Value Calculation

        
        return { summary: newSummary, categoryData: newCategoryData };
    }, [lifeCarePlan]);

    if (!lifeCarePlan) {
        return (
            <Box className="report-section" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>Life Care Plan Summary</Typography>
                <Typography>No Life Care Plan data available.</Typography>
            </Box>
        );
    }

    return (
        <>
            <Box className="report-section" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>Life Care Plan Summary</Typography>
                <Typography sx={{ mb: 0.5 }}><strong>Total Annual Cost:</strong> {formatCurrency(summary.totalAnnualCost)}</Typography>
                <Typography sx={{ mb: 0.5 }}><strong>Total Future Value (Stub):</strong> {formatCurrency(summary.totalFutureValue)}</Typography>
                <Typography sx={{ mb: 0.5, fontWeight: 'bold' }}><strong>Total Present Value (Stub):</strong> {formatCurrency(summary.totalPresentValue)}</Typography>
                
                {Object.entries(lifeCarePlanCategoryConfig).map(([category, config]) => (
                    <RenderCategoryTable
                        key={category}
                        title={config.title}
                        data={categoryData[category]?.items}
                        columns={config.columns.map(c => ({ ...c, headerName: c.reportHeader || c.headerName }))}
                    />
                ))}
            </Box>
            <Divider sx={{ my: 2 }} />
        </>
    );
};

LifeCarePlanSummary.addPdfContent = (pdf, reportData, y) => {
    const { lifeCarePlan } = reportData || {};
    if (!lifeCarePlan) return y;

    pdf.setFont('helvetica', 'bold');
    pdf.text('Life Care Plan Summary', 15, y);
    y += 7;
    pdf.setFont('helvetica', 'normal');

    // Summary totals would go here
    // TODO: Add summary totals to PDF

    Object.entries(lifeCarePlanCategoryConfig).forEach(([category, config]) => {
        const items = lifeCarePlan[category] || [];
        if (items.length > 0) {
            y += 4;
            pdf.setFont('helvetica', 'bold');
            pdf.text(config.title, 15, y);
            y += 4;
            pdf.setFont('helvetica', 'normal');

            const head = [config.columns.map(c => c.reportHeader || c.headerName)];
            const body = items.map(item => config.columns.map(col => {
                const value = item[col.field] || '-';
                return col.isCurrency ? formatCurrency(value) : value;
            }));

            autoTable(pdf, {
                startY: y,
                head: head,
                body: body,
                theme: 'grid',
                styles: { fontSize: 9 },
            });
            y = pdf.lastAutoTable.finalY + 8;
        }
    });

    return y;
};

export default LifeCarePlanSummary;